import type { AxiosResponse } from 'axios';
import { bunja, type Bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { atom, useAtom, useAtomValue, type Atom } from 'jotai';
import { atomWithMutation, queryClientAtom } from 'jotai-tanstack-query';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';

import type {
  GetDefiYieldDetailsResponse,
  GetDefiYieldMintOverviewResponse,
  PostDefiYieldMintConfirmRequest,
  PostDefiYieldMintConfirmResponse,
  PostDefiYieldMintPreviewRequest,
  PostDefiYieldMintPreviewResponse,
} from '@flipster/apis/v1/dynamic-earn/types';
import { FormattedMessage, i18nBunja, type MessageKey } from '@flipster/i18n';
import { useRouter } from '@flipster/i18n/routing';
import { overlayBunja, useOverlay } from '@flipster/overlay';
import { Slider } from '@flipster/react/design-system-legacy/slider';
import { Checkbox } from '@flipster/react/design-system/checkbox';
import { Modal } from '@flipster/react/design-system/modal';
import Formatted from '@flipster/react/design-utils/formatted';
import { FormattedNumberInput } from '@flipster/react/design-utils/formatted-input';
import ArrowRightIcon from '@flipster/react/icon/generated/ArrowRightIcon';
import CautionCircleIcon from '@flipster/react/icon/generated/CautionCircleIcon';
import CheckCircleIcon from '@flipster/react/icon/generated/CheckCircleIcon';
import { getLocalStorageAtom } from '@flipster/react/misc/state/use-local-storage';
import { css } from '@flipster/styled-system/css';
import { center, hstack, stack, vstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { UsdtCurrency, UsdtPrecision } from '@flipster/utils/constants/currency';
import { getErrorCode, getErrorMessage } from '@flipster/utils/error';
import { getFlipsterConfigsV1Key } from '@flipster/utils/localStorage';
import Num, { type BigNumber } from '@flipster/utils/num';
import segment from '@flipster/utils/segment';
import { DynamicEarnEvent } from '@flipster/utils/segment/events/dynamic-earn';

import { CurrencyDisplay } from '../../../misc/display';
import { authBunja } from '../../../shared/auth/state';
import { useV1Currencies } from '../../../shared/currency/use-v1-currencies';
import { AgreeCheckbox, type TermsLink } from '../../main-page/misc/agree-checkbox';
import { getNumberColorWithNegativeWhite } from '../color';

// ─── Types ─────────────────────────────────────────────────

export type SubscribeResult =
  | { type: 'success'; amount: string }
  | { type: 'liquidationRisk' }
  | { type: 'error'; message: string };

interface MintOverviewBunjaValue {
  mintOverviewAtom: Atom<GetDefiYieldMintOverviewResponse | undefined>;
  availableBalanceAtom: Atom<BigNumber>;
  minAmountAtom: Atom<string>;
  estimatedProcessingHoursAtom: Atom<number>;
}

interface DetailsBunjaValue {
  detailsAtom: Atom<GetDefiYieldDetailsResponse | undefined>;
}

interface CollateralBunjaValue {
  usdtCollateralRateAtom: Atom<BigNumber>;
  productCollateralRateAtom: Atom<BigNumber>;
}

interface SubscribeModalConfig {
  mintOverviewBunja: Bunja<MintOverviewBunjaValue>;
  detailsBunja: Bunja<DetailsBunjaValue>;
  collateralBunja: Bunja<CollateralBunjaValue>;
  previewApi: (params: PostDefiYieldMintPreviewRequest) => Promise<AxiosResponse<PostDefiYieldMintPreviewResponse>>;
  confirmApi: (params: PostDefiYieldMintConfirmRequest) => Promise<AxiosResponse<PostDefiYieldMintConfirmResponse>>;
  overviewQueryKey: string;
  liquidationRiskErrorCode: string;
  liquidationRiskLocalStorageKey: Parameters<typeof getFlipsterConfigsV1Key>[1];
  productName: string;
  currency: string;
  terms: TermsLink[];
  toLabelId: MessageKey;
}

export function createSubscribeModal(config: SubscribeModalConfig) {
  const {
    mintOverviewBunja: mintBunja,
    detailsBunja: detailsBunjaRef,
    collateralBunja,
    previewApi,
    confirmApi,
    overviewQueryKey,
    liquidationRiskErrorCode,
    liquidationRiskLocalStorageKey,
    productName,
    currency,
    terms,
    toLabelId,
  } = config;

  // ─── Liquidation Risk Checked Bunja ──────────────────────

  const liquidationRiskCheckedBunja = bunja(() => {
    const { userAtom } = bunja.use(authBunja);
    const checkedAtom = atom(
      (get) => {
        const user = get(userAtom);
        if (!user) return false;
        const key = getFlipsterConfigsV1Key(user.userId, liquidationRiskLocalStorageKey);
        const localStorageAtom = getLocalStorageAtom(key, false);
        return get(localStorageAtom) as boolean;
      },
      (get, set, newValue: boolean) => {
        const user = get(userAtom);
        if (!user) return;
        const key = getFlipsterConfigsV1Key(user.userId, liquidationRiskLocalStorageKey);
        const localStorageAtom = getLocalStorageAtom(key, false);
        set(localStorageAtom, newValue);
      },
    );
    return { checkedAtom };
  });
  liquidationRiskCheckedBunja.debugLabel = `liquidationRiskCheckedBunja(${productName})`;

  // ─── Subscribe Modal Bunja ───────────────────────────────

  const modalBunja = bunja(() => {
    const store = bunja.use(JotaiStoreScope);
    const overlay = bunja.use(overlayBunja);
    const { getT } = bunja.use(i18nBunja);
    const t = getT();
    const { availableBalanceAtom, minAmountAtom } = bunja.use(mintBunja);

    const agreedAtom = atom(false);
    const amountAtom = atom<string>('');
    const isDirtyAtom = atom((get) => get(amountAtom) !== '');

    const percentageAtom = atom(
      (get) => {
        const availableBalance = get(availableBalanceAtom);
        const amount = get(amountAtom);
        if (availableBalance.isZero()) return 0;
        const amountNum = Num(amount).isFinite() ? Num(amount) : Num(0);
        const percentage = amountNum.div(availableBalance).times(100).toNumber();
        return Math.min(percentage, 100);
      },
      (get, set, newPercentage: number) => {
        const availableBalance = get(availableBalanceAtom);
        const calculatedAmount = availableBalance.div(100).times(newPercentage).toFixed(2);
        set(amountAtom, calculatedAmount);
      },
    );

    const errorMessageAtom = atom((get) => {
      const minAmount = get(minAmountAtom);
      const amount = get(amountAtom);
      const isDirty = get(isDirtyAtom);
      if (!isDirty) return null;
      if (Num(amount).lt(minAmount)) {
        return t('Earn.r251204.Min_subscription_required', { amountSymbol: `${minAmount} USDT` });
      }
      return null;
    });

    const isValidAtom = atom((get) => {
      const isDirty = get(isDirtyAtom);
      const agreed = get(agreedAtom);
      const errorMessage = get(errorMessageAtom);
      return isDirty && agreed && !errorMessage;
    });

    const mintPreviewMutaionAtom = atomWithMutation((get) => {
      const amount = get(amountAtom);
      const fromCurrency = UsdtCurrency;
      const fromAmount = Num(amount).toString();
      return { mutationFn: () => previewApi({ fromCurrency, fromAmount }) };
    });

    const previewIdAtom = atom<number | undefined>(undefined);
    const mintConfirmMutaionAtom = atomWithMutation((get) => {
      const previewId = get(previewIdAtom);
      const queryClient = get(queryClientAtom);
      return {
        mutationFn: () => confirmApi({ previewId: previewId! }),
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: [overviewQueryKey] });
        },
      };
    });

    async function subscribe() {
      let result: SubscribeResult;
      try {
        const previewResult = await store.get(mintPreviewMutaionAtom).mutateAsync();
        if (previewResult.data?.id) {
          store.set(previewIdAtom, previewResult.data.id);
          await store.get(mintConfirmMutaionAtom).mutateAsync();
          result = { type: 'success', amount: store.get(amountAtom) };
        } else {
          return;
        }
      } catch (error) {
        if (getErrorCode(error) === liquidationRiskErrorCode) {
          result = { type: 'liquidationRisk' };
        } else {
          result = { type: 'error', message: getErrorMessage(error) };
        }
      }
      overlay.open(({ isOpen, onClose, unmount }) => (
        <SubscribeResultModal isOpen={isOpen} onClose={onClose} onExit={unmount} result={result} />
      ));
    }

    const isSubmittingAtom = atom((get) => {
      return get(mintPreviewMutaionAtom).isPending || get(mintConfirmMutaionAtom).isPending;
    });

    return { agreedAtom, amountAtom, percentageAtom, errorMessageAtom, isValidAtom, subscribe, isSubmittingAtom };
  });
  modalBunja.debugLabel = `subscribeModalBunja(${productName})`;

  // ─── Helper: use collateral rates from config bunja ─────

  function useCollateralRates() {
    const { usdtCollateralRateAtom, productCollateralRateAtom } = useBunja(collateralBunja);
    const usdtCollateralRate = useAtomValue(usdtCollateralRateAtom);
    const productCollateralRate = useAtomValue(productCollateralRateAtom);
    return { usdtCollateralRate, productCollateralRate };
  }

  // ─── Subscribe Result Modal ──────────────────────────────

  const SubscribeResultModal = ({
    isOpen,
    onClose,
    onExit,
    result,
  }: {
    isOpen: boolean;
    onClose: () => void;
    onExit: () => void;
    result: SubscribeResult;
  }) => {
    return (
      <Modal.Root isOpen={isOpen} onClose={onClose} onExit={onExit}>
        {result.type === 'success' && <SuccessContent amount={result.amount} onClose={onClose} />}
        {result.type === 'liquidationRisk' && <LiquidationRiskContent onClose={onClose} />}
        {result.type === 'error' && <ErrorContent message={result.message} onClose={onClose} />}
      </Modal.Root>
    );
  };

  const SuccessContent = ({ amount, onClose }: { amount: string; onClose: () => void }) => {
    const router = useRouter();
    const { usdtCollateralRate, productCollateralRate } = useCollateralRates();
    const { estimatedProcessingHoursAtom } = useBunja(mintBunja);
    const estimatedProcessingHours = useAtomValue(estimatedProcessingHoursAtom);

    return (
      <>
        <Modal.Content>
          <div className={stack({ gap: 32 })}>
            <div className={vstack({ gap: 8 })}>
              <CheckCircleIcon size={48} color={token('colors.content.accent.info')} />
              <p className={css({ textStyle: 'Title/L', color: 'content.default.level-5', textAlign: 'center' })}>
                <FormattedMessage id='Earn.r251204.Subscription_request_submitted' />
              </p>
            </div>
            <div className={stack({ gap: 10 })}>
              <div className={stack({ gap: 16, py: 16, px: 20, rounded: 12, bg: 'background.default.level-1' })}>
                <div className={stack({ gap: 16 })}>
                  <div className={vstack({ gap: 2 })}>
                    <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-5' })}>
                      <FormattedMessage id='Earn.r251204.You_subscribed' />
                    </p>
                    <CurrencyDisplay
                      currency={UsdtCurrency}
                      icon={<CurrencyDisplay.Icon size={24} />}
                      mainLabel={
                        <p className={css({ textStyle: 'Title/S/Monospace', color: 'content.default.level-5' })}>
                          <Formatted.Number value={amount} suffix={UsdtCurrency} precision={UsdtPrecision} toFixed />
                        </p>
                      }
                    />
                    <p className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-5' })}>
                      {/* @ts-expect-error — toLabelId keys do not require values */}
                      <FormattedMessage id={toLabelId} />
                    </p>
                  </div>
                  <div
                    className={hstack({
                      justify: 'space-between',
                      pt: 16,
                      borderColor: 'border.default.level-1',
                      borderTopWidth: '1px',
                      borderTopStyle: 'solid',
                    })}
                  >
                    <span className={css({ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-3' })}>
                      <FormattedMessage id='Autogen_common.r251126.s_Est_processing_time' />
                    </span>
                    <span className={css({ textStyle: 'Text/L/Monospace/Regular', color: 'content.default.level-5' })}>
                      <FormattedMessage
                        id='Earn.r251204.Within_hours_duration'
                        values={{ hour: estimatedProcessingHours as number }}
                      />
                    </span>
                  </div>
                </div>
              </div>

              <div className={stack({ gap: 16, py: 16, px: 20, bg: 'background.default.level-1' })}>
                <div className={vstack({ gap: 2 })}>
                  <p className={css({ textStyle: 'Title/XS', color: 'content.state.positive' })}>
                    <FormattedMessage id='Earn.r260203.Available_for_cross_margin' />
                  </p>
                  <p className={css({ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-3' })}>
                    <FormattedMessage id='Earn.r260304.Subscription_complete_avail_for_cross_margin_description' />
                  </p>
                </div>
                <div
                  className={stack({
                    gap: 4,
                    pt: 16,
                    borderColor: 'border.default.level-1',
                    borderTopWidth: '1px',
                    borderTopStyle: 'solid',
                  })}
                >
                  <div className={hstack({ justify: 'space-between' })}>
                    <span className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
                      <FormattedMessage id='Common.r251204.Processing_status' />
                    </span>
                    <span className={css({ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-5' })}>
                      <Formatted.Percent value={usdtCollateralRate} isRate precision={0} />
                    </span>
                  </div>
                  <div className={hstack({ justify: 'space-between' })}>
                    <span className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
                      <FormattedMessage id='Earn.r260304.Subscription_complete_avail_for_cross_margin_completion' />
                    </span>
                    <span className={css({ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-5' })}>
                      <Formatted.Percent value={productCollateralRate} isRate precision={0} />
                    </span>
                  </div>
                </div>
              </div>

              <ul
                className={stack({
                  gap: 4,
                  list: 'disc',
                  pl: 16,
                  textStyle: 'Text/S/Regular',
                  color: 'content.default.level-3',
                })}
              >
                <li>
                  <FormattedMessage id='Earn.r260304.Subscription_complete_disclaimer_1' />
                </li>
                <li>
                  <FormattedMessage id='Earn.r251204.Final_amount_rounding_notice' />
                </li>
                <li>
                  <FormattedMessage id='Earn.r260108.Processing_notification_notice' />
                </li>
              </ul>
            </div>
          </div>
        </Modal.Content>
        <Modal.Footer>
          <Modal.Button
            size='large'
            label={<FormattedMessage id='Common.r250401.Done' />}
            onClick={() => {
              onClose();
              router.push('/dynamic-earn');
            }}
          />
        </Modal.Footer>
      </>
    );
  };

  const LiquidationRiskContent = ({ onClose }: { onClose: () => void }) => {
    const overlay = useOverlay();
    const { usdtCollateralRate, productCollateralRate } = useCollateralRates();

    useEffect(() => {
      segment.track(DynamicEarnEvent.DynamicEarnLiquidationAlertPopupView);
    }, []);

    return (
      <>
        <Modal.Content>
          <div className={stack({ gap: 32 })}>
            <div className={vstack({ gap: 8 })}>
              <CautionCircleIcon size={48} color={token('colors.content.state.warning')} />
              <p className={css({ textStyle: 'Title/L', color: 'content.default.level-5', textAlign: 'center' })}>
                <FormattedMessage id='Earn.r260304.Increased_liquidation_risk_title' />
              </p>
            </div>
            <p className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-3', textAlign: 'center' })}>
              <FormattedMessage id='Earn.r260304.Increased_liquidation_risk_description' />
            </p>
            <CollateralComparison
              currency={currency}
              usdtCollateralRate={usdtCollateralRate}
              productCollateralRate={productCollateralRate}
            />
          </div>
        </Modal.Content>
        <Modal.Footer>
          <Modal.Button
            size='large'
            label={<FormattedMessage id='Earn.r260304.Increased_liquidation_risk_button' />}
            onClick={() => {
              segment.track(DynamicEarnEvent.DynamicEarnLiquidationAlertAdjustClick);
              onClose();
              overlay.open(({ isOpen, onClose, unmount }) => (
                <SubscribeModal isOpen={isOpen} onClose={onClose} onExit={unmount} />
              ));
            }}
          />
        </Modal.Footer>
      </>
    );
  };

  const ErrorContent = ({ message, onClose }: { message: string; onClose: () => void }) => (
    <>
      <Modal.Content>
        <div className={stack({ gap: 32 })}>
          <div className={vstack({ gap: 8 })}>
            <CautionCircleIcon size={48} color={token('colors.content.state.negative')} />
            <p className={css({ textStyle: 'Title/L', color: 'content.default.level-5', textAlign: 'center' })}>
              <FormattedMessage id='Common.r250401.Error' />
            </p>
          </div>
          <p className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-3', textAlign: 'center' })}>
            {message}
          </p>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.Button size='large' label={<FormattedMessage id='Common.r250401.Done' />} onClick={onClose} />
      </Modal.Footer>
    </>
  );

  // ─── Collateral Comparison (shared by result + risk check) ─

  const CollateralComparison = ({
    currency: cur,
    usdtCollateralRate,
    productCollateralRate,
  }: {
    currency: string;
    usdtCollateralRate: BigNumber;
    productCollateralRate: BigNumber;
  }) => (
    <div
      className={stack({
        gap: 16,
        p: 20,
        borderColor: 'border.default.level-1',
        borderWidth: '1px',
        borderStyle: 'solid',
        rounded: 12,
      })}
    >
      <div className={vstack({ gap: 8 })}>
        <CurrencyDisplay
          currency={cur}
          icon={<CurrencyDisplay.Icon size={20} />}
          mainLabel={<CurrencyDisplay.Label value='fullName' />}
        />
        <p className={css({ textStyle: 'Title/XS', color: 'content.default.level-5', textAlign: 'center' })}>
          <FormattedMessage id='Earn.r260203.Available_for_cross_margin' />
        </p>
      </div>
      <div className={hstack({ justify: 'center', gap: 8, p: 12, rounded: 8, bg: 'background.default.level-1' })}>
        <div className={stack({ flex: 1, align: 'center', gap: 2 })}>
          <span className={css({ textStyle: 'Title/XS/Monospace', color: 'content.default.level-5' })}>
            <Formatted.Percent value={usdtCollateralRate} isRate precision={0} />
          </span>
          <span className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
            <FormattedMessage id='Earn.r260304.How_dynamic_earn_affects_your_collateral_before_subscription' />
          </span>
        </div>
        <ArrowRightIcon size={16} color={token('colors.content.default.level-5')} />
        <div className={stack({ flex: 1, align: 'center', gap: 2 })}>
          <span className={css({ textStyle: 'Title/XS/Monospace', color: 'content.default.level-5' })}>
            <Formatted.Percent value={productCollateralRate} isRate precision={0} />
          </span>
          <span className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-3' })}>
            <FormattedMessage id='Earn.r260304.How_dynamic_earn_affects_your_collateral_after_completion' />
          </span>
        </div>
      </div>
    </div>
  );

  // ─── Liquidation Risk Check Modal ────────────────────────

  const LiquidationRiskCheckModal = ({
    isOpen,
    onClose,
    onExit,
    onConfirm,
  }: {
    isOpen: boolean;
    onClose: () => void;
    onExit: () => void;
    onConfirm: () => void;
  }) => {
    const { checkedAtom } = useBunja(liquidationRiskCheckedBunja);
    const [checked, setChecked] = useAtom(checkedAtom);
    const { usdtCollateralRate, productCollateralRate } = useCollateralRates();

    useEffect(() => {
      segment.track(DynamicEarnEvent.DynamicEarnCollateralNoticeModalView);
    }, []);

    return (
      <Modal.Root isOpen={isOpen} onClose={onClose} onExit={onExit}>
        <Modal.Header>
          <Modal.Title label={<FormattedMessage id='Earn.r260304.How_dynamic_earn_affects_your_collateral_title' />} />
        </Modal.Header>
        <Modal.Content>
          <div className={stack({ gap: 16 })}>
            <p className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-5' })}>
              <FormattedMessage id='Earn.r260304.How_dynamic_earn_affects_your_collateral_description' />
            </p>
            <CollateralComparison
              currency={currency}
              usdtCollateralRate={usdtCollateralRate}
              productCollateralRate={productCollateralRate}
            />
            <Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)} css={{ pt: 16 }}>
              <FormattedMessage id='Common.r251204.Don_t_show_message_again' />
            </Checkbox>
          </div>
        </Modal.Content>
        <Modal.Footer>
          <Modal.Button
            size='large'
            label={<FormattedMessage id='Common.r250918.Got_it' />}
            onClick={() => {
              onClose();
              onConfirm();
              segment.track(DynamicEarnEvent.DynamicEarnCollateralNoticeConfirmClick);
            }}
          />
        </Modal.Footer>
      </Modal.Root>
    );
  };

  // ─── Subscribe Modal ─────────────────────────────────────

  const SubscribeModal = ({
    isOpen,
    onClose,
    onExit,
  }: {
    isOpen: boolean;
    onClose: () => void;
    onExit: () => void;
  }) => {
    const t = useTranslations();
    const { currenciesKeyByCurrency } = useV1Currencies();
    const { mintOverviewAtom, availableBalanceAtom, minAmountAtom, estimatedProcessingHoursAtom } = useBunja(mintBunja);
    const { agreedAtom, amountAtom, percentageAtom, errorMessageAtom, isValidAtom, isSubmittingAtom, subscribe } =
      useBunja(modalBunja);
    const mintOverview = useAtomValue(mintOverviewAtom);
    const [agreed, setAgreed] = useAtom(agreedAtom);
    const availableBalance = useAtomValue(availableBalanceAtom);
    const [amount, setAmount] = useAtom(amountAtom);
    const [percentage, setPercentage] = useAtom(percentageAtom);
    const errorMessage = useAtomValue(errorMessageAtom);
    const minAmount = useAtomValue(minAmountAtom);
    const isValid = useAtomValue(isValidAtom);
    const isSubmitting = useAtomValue(isSubmittingAtom);
    const estimatedProcessingHours = useAtomValue(estimatedProcessingHoursAtom);

    const { detailsAtom } = useBunja(detailsBunjaRef);
    const details = useAtomValue(detailsAtom);

    return (
      <Modal.Root isOpen={isOpen} onClose={onClose} onExit={onExit}>
        <Modal.Header>
          <Modal.Title label={<FormattedMessage id='Earn.r251204.Enter_subscription_amount' />} />
        </Modal.Header>
        <Modal.Content>
          <div className={stack({ gap: 4 })}>
            <div className={hstack({ gap: 8, py: 12, pl: 16, pr: 12, rounded: 8, bg: 'background.default.level-1' })}>
              <span className={css({ flex: 1, textStyle: 'Text/M/Regular', color: 'content.default.level-5' })}>
                {currenciesKeyByCurrency?.[mintOverview?.providerCurrency ?? '']?.englishName}
              </span>
              <div className={hstack({ gap: 4 })}>
                <span
                  className={css({
                    textStyle: 'Text/M/Monospace/Regular',
                    color: getNumberColorWithNegativeWhite(mintOverview?.estimateApr),
                  })}
                >
                  <FormattedMessage
                    id='Common.r251204.Est_apr_percent'
                    values={{
                      percent: (
                        <Formatted.Percent
                          key='percent'
                          value={mintOverview?.estimateApr}
                          precision={2}
                          toFixed
                          isRate
                        />
                      ),
                    }}
                  />
                </span>
              </div>
            </div>
            <div className={stack({ gap: 20, px: 16, pt: 16, pb: 20, rounded: 8, bg: 'background.default.level-1' })}>
              <div className={stack({ gap: 8 })}>
                <div className={hstack({ justify: 'space-between' })}>
                  <span className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>
                    <FormattedMessage id='Earn.r251204.You_subscribe' />
                  </span>
                  <span
                    className={css({
                      textStyle: 'Text/S/Monospace/Regular',
                      color: 'content.default.level-3',
                      textAlign: 'right',
                    })}
                  >
                    <FormattedMessage id='Common.r251107.Available' />{' '}
                    <Formatted.Number
                      value={availableBalance}
                      suffix={UsdtCurrency}
                      precision={UsdtPrecision}
                      toFixed
                    />
                  </span>
                </div>
                <div className={hstack({ gap: 8 })}>
                  <FormattedNumberInput
                    value={amount}
                    onChange={setAmount}
                    placeholder={t('Common.r251204.Min_amount', { amount: minAmount as string })}
                    inputMode='decimal'
                    precision={UsdtPrecision}
                    max={availableBalance.toString()}
                    className={css({ flex: 1, textStyle: 'Title/M/Monospace', color: 'content.default.level-5' })}
                  />
                  <div className={center({ w: 88, h: 36, rounded: 1000, bg: 'background.accent.secondary.tint' })}>
                    <CurrencyDisplay
                      currency={UsdtCurrency}
                      icon={<CurrencyDisplay.Icon size={20} space={4} />}
                      mainLabel={<CurrencyDisplay.Label value='shortName' />}
                    />
                  </div>
                </div>
                <div className={hstack({ h: 17, color: 'content.state.negative' })}>
                  {errorMessage && <p>{errorMessage}</p>}
                </div>
              </div>
              <Slider
                value={percentage}
                onChange={setPercentage}
                marks={[0, 25, 50, 75, 100]}
                getLabel={(mark) => `${mark}%`}
                css={{ '& button': { textStyle: 'Text/S/Regular', color: 'content.default.level-2!' } }}
              />
            </div>
            <div
              className={stack({
                gap: 8,
                py: 12,
                px: 16,
                borderColor: 'border.default.level-1',
                borderWidth: '1px',
                borderStyle: 'solid',
                rounded: 8,
              })}
            >
              <div className={hstack({ justify: 'space-between' })}>
                <span className={css({ textStyle: 'Text/S/Monospace/Regular', color: 'content.default.level-2' })}>
                  <FormattedMessage id='Autogen_common.r251126.s_Est_processing_time' />
                </span>
                <span
                  className={css({
                    textStyle: 'Text/M/Monospace/Regular',
                    color: 'content.default.level-5',
                    textAlign: 'right',
                  })}
                >
                  <FormattedMessage
                    id='Earn.r251204.Within_hours_duration'
                    values={{ hour: estimatedProcessingHours as number }}
                  />
                </span>
              </div>
              <div className={hstack({ justify: 'space-between' })}>
                <span className={css({ textStyle: 'Text/S/Monospace/Regular', color: 'content.default.level-2' })}>
                  <FormattedMessage id='Common.r251204.Fee' />
                </span>
                <span
                  className={css({
                    textStyle: 'Text/M/Monospace/Regular',
                    color: 'content.default.level-5',
                    textAlign: 'right',
                  })}
                >
                  <Formatted.Percent value={mintOverview?.feeRate} precision={2} toFixed isRate />
                </span>
              </div>
              <div
                className={css({
                  borderTopWidth: '1px',
                  borderTopStyle: 'solid',
                  borderColor: 'border.default.level-1',
                  pt: 8,
                })}
              >
                <div className={hstack({ justify: 'space-between' })}>
                  <span className={css({ textStyle: 'Text/S/Monospace/Regular', color: 'content.default.level-2' })}>
                    <FormattedMessage id='Earn.r260224.Redemption_option' />
                  </span>
                  <div className={stack({ align: 'flex-end', gap: 2 })}>
                    <span className={css({ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-5' })}>
                      <FormattedMessage id='Earn.r260108.Instant' /> (
                      <Formatted.Percent value={details?.instantRedemptionFee} isRate toFixed precision={2} />{' '}
                      <FormattedMessage id='Earn.r260224.With_cap' />)
                    </span>
                    <span className={css({ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-5' })}>
                      <FormattedMessage id='Earn.r260108.Standard' /> (
                      <Formatted.Percent value={details?.standardRedemptionFee} isRate toFixed precision={2} />
                      {', '}
                      {mintOverview?.redemptionProcessingTimeHours ?? '-'}h)
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <p className={css({ mt: 8, textStyle: 'Text/S/Regular', color: 'content.default.level-3' })}>
            <FormattedMessage id='Earn.r260304.Subscription_preview_description' />
          </p>
          <div className={css({ mt: 32 })}>
            <AgreeCheckbox agreed={agreed} onChange={setAgreed} terms={terms} />
          </div>
        </Modal.Content>
        <Modal.Footer>
          <Modal.Button
            label={<FormattedMessage id='Earn.r251204.Subscribe' />}
            size='large'
            disabled={!isValid}
            isLoading={isSubmitting}
            onClick={async () => {
              segment.track(DynamicEarnEvent.DynamicEarnSubscriptionConfirmClick, {
                product_name: productName,
                subscribed_amount: amount,
              });
              await subscribe();
              onClose();
            }}
          />
        </Modal.Footer>
      </Modal.Root>
    );
  };

  return {
    SubscribeModal,
    subscribeModalBunja: modalBunja,
    LiquidationRiskCheckModal,
    liquidationRiskCheckedBunja,
  };
}
